import { useCallback, useEffect, useRef, useMemo } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import logger from "../utils/logger";
import api from "../services/api";
import {
  SESSION_STORAGE_KEY,
  SCROLL_POSITION_KEY,
} from "../components/deals/dealList/constants";
import { normalizeMealTimeForApi } from "../utils/mealTimeFilter";

export const useDealsCache = () => {
  const getSessionCache = useCallback(() => {
    try {
      const cached = sessionStorage.getItem(SESSION_STORAGE_KEY);
      if (cached) {
        const parsed = JSON.parse(cached);
        logger.info("📖 Reading from session storage", {
          cacheKey: parsed.cacheKey,
          dealsCount: parsed.list?.length || 0,
          hasMore: parsed.hasMore,
        });
        return parsed;
      }
      return null;
    } catch (error) {
      logger.error("Error reading from session storage:", error);
      return null;
    }
  }, []);

  const setSessionCache = useCallback((data) => {
    try {
      sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(data));
      logger.info("💾 Writing to session storage", {
        cacheKey: data.cacheKey,
        dealsCount: data.list?.length || 0,
        hasMore: data.hasMore,
      });
    } catch (error) {
      logger.error("Error writing to session storage:", error);
    }
  }, []);

  const clearSessionCache = useCallback(() => {
    try {
      const existingCache = getSessionCache();
      if (existingCache) {
        logger.info("🗑️ Clearing session storage cache", {
          previousCacheKey: existingCache.cacheKey,
          previousDealsCount: existingCache.list?.length || 0,
        });
      }
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
    } catch (error) {
      logger.error("Error clearing session storage:", error);
    }
  }, [getSessionCache]);

  const restoreScrollPosition = useCallback(() => {
    try {
      const savedPosition = sessionStorage.getItem(SCROLL_POSITION_KEY);
      if (savedPosition) {
        window.scrollTo(0, parseInt(savedPosition, 10));
        sessionStorage.removeItem(SCROLL_POSITION_KEY);
      }
    } catch (error) {
      logger.error("Error restoring scroll position:", error);
    }
  }, []);

  return {
    getSessionCache,
    setSessionCache,
    clearSessionCache,
    restoreScrollPosition,
  };
};

export const useInfiniteScroll = ({
  hasMore,
  nextCursor,
  loading,
  loadingMore,
  fetchDeals,
  desktopLoadMoreRef,
  mobileLoadMoreRef,
}) => {
  const observerRef = useRef(null);

  useEffect(() => {
    if (loading || loadingMore || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && nextCursor && !loadingMore) {
          fetchDeals(nextCursor, true);
        }
      },
      { threshold: 0.1 },
    );

    observerRef.current = observer;

    const attachObserver = () => {
      if (desktopLoadMoreRef.current)
        observer.observe(desktopLoadMoreRef.current);
      if (mobileLoadMoreRef.current)
        observer.observe(mobileLoadMoreRef.current);
    };

    attachObserver();

    if (!desktopLoadMoreRef.current && !mobileLoadMoreRef.current) {
      const timeouts = [100, 500, 1000].map((delay) =>
        setTimeout(attachObserver, delay),
      );
      return () => {
        timeouts.forEach(clearTimeout);
        observerRef.current?.disconnect();
      };
    }

    return () => observerRef.current?.disconnect();
  }, [
    hasMore,
    nextCursor,
    fetchDeals,
    loading,
    loadingMore,
    desktopLoadMoreRef,
    mobileLoadMoreRef,
  ]);

  useEffect(() => {
    if (observerRef.current && hasMore && !loading && !loadingMore) {
      const timeoutId = setTimeout(() => {
        if (desktopLoadMoreRef.current)
          observerRef.current.observe(desktopLoadMoreRef.current);
        if (mobileLoadMoreRef.current)
          observerRef.current.observe(mobileLoadMoreRef.current);
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [hasMore, loading, loadingMore, desktopLoadMoreRef, mobileLoadMoreRef]);

  return observerRef;
};

export const useDeals = (filters, isMobile, options = {}) => {
  const {
    refetchOnMount = false,
    refetchOnWindowFocus = false,
    refetchOnReconnect = false,
    enabled = true,
    ...queryOptions
  } = options;

  const { restoreScrollPosition } = useDealsCache();

  const area = filters?.area || "";
  const date = filters?.date || "";
  const timeOfDay = normalizeMealTimeForApi(filters?.timeOfDay);
  const category = filters?.category || "";
  const cuisine = filters?.cuisine || "";
  const tags = filters?.tags || "";
  const search = filters?.search || "";
  const priceBand = filters?.priceBand || "";
  const discountType = filters?.discountType || "";

  const queryKey = useMemo(
    () => [
      "deals",
      {
        area,
        date,
        timeOfDay,
        category,
        cuisine,
        tags,
        search,
        priceBand,
        discountType,
        pageSize: 50,
      },
    ],
    [area, date, timeOfDay, category, cuisine, tags, search, priceBand, discountType],
  );

  const queryFn = useCallback(
    async ({ pageParam = null }) => {
      const params = {
        activeOnly: true,
        pageSize: 50,
        ...(typeof pageParam === "number" && pageParam > 0
          ? { page: pageParam }
          : { page: 1 }),
        ...(area && area !== "" && { area }),
        ...(date && { date }),
        ...(timeOfDay && { time_of_day: timeOfDay }),
        ...(category && { category }),
        ...(cuisine && { cuisine }),
        ...(tags && { tags }),
        ...(search?.trim() && { search: search.trim() }),
        ...(priceBand && priceBand !== "any" && { price_band: priceBand }),
        ...(discountType && { discount_type: discountType }),
      };

      const res = await api.get("/deals", { params });
      const payload = res?.data;
      const raw = payload?.data || [];
      const pageInfoIn = payload?.pageInfo || {};
      const pageSize = pageInfoIn.pageSize ?? params.pageSize ?? 50;
      const serverHasMore = !!pageInfoIn.hasMore;
      const hasMore = serverHasMore && raw.length >= pageSize;

      logger.info("📡 /deals", {
        params,
        dataCount: raw.length,
        hasMore,
      });
      return {
        data: raw,
        pageInfo: { ...pageInfoIn, hasMore },
      };
    },
    [area, date, timeOfDay, category, cuisine, tags, search, priceBand, discountType],
  );

  const query = useInfiniteQuery({
    queryKey,
    queryFn,
    enabled,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.pageInfo?.hasMore ? allPages.length + 1 : undefined,
    initialPageParam: 1,
    staleTime: 2 * 60 * 1000, // 2 min: use cache when revisiting tab, avoid refetch on every mount/focus
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
    refetchOnMount,
    refetchOnWindowFocus,
    refetchOnReconnect,
    ...queryOptions, // Allow caller to override any other options
  });

  const deals = useMemo(
    () => query.data?.pages.flatMap((page) => page.data) || [],
    [query.data],
  );

  const restoreScrollPositionRef = useRef(restoreScrollPosition);
  useEffect(() => {
    restoreScrollPositionRef.current = restoreScrollPosition;
  }, [restoreScrollPosition]);

  useEffect(() => {
    if (query.data && !query.isFetching && !query.isLoading) {
      setTimeout(() => restoreScrollPositionRef.current(), 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.data]);

  const prevQueryKeyRef = useRef(queryKey);
  useEffect(() => {
    if (
      JSON.stringify(prevQueryKeyRef.current) !== JSON.stringify(queryKey) &&
      query.data &&
      !query.isFetching
    ) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      prevQueryKeyRef.current = queryKey;
    }
  }, [queryKey, query.data, query.isFetching]);

  const queryRef = useRef(query);
  queryRef.current = query;

  const fetchDeals = useCallback(
    (cursor, append) => {
      if (append && cursor) {
        queryRef.current.fetchNextPage();
      } else {
        queryRef.current.refetch();
      }
    },
    [], // Empty deps - function is stable
  );

  return {
    deals,
    loading: query.isLoading && !query.data,
    status: query.isError ? "error" : query.isSuccess ? "success" : "idle",
    loadingMore: query.isFetchingNextPage,
    hasMore: query.hasNextPage,
    // For page-based pagination, "nextCursor" is just the next page number.
    nextCursor: query.hasNextPage ? (query.data?.pages?.length || 0) + 1 : null,
    fetchDeals,
    refetch: query.refetch, // Expose refetch directly
  };
};
